import type { FC } from "react";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { type DropdownItem, SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import type { Department } from "../../../../contexts/AuthProvider";
import { useDepartmentFlags } from "../../../../hooks/useDepartmentFlags";
import type { RoleColorResolver } from "../../../../utils/categoricalColors";
import { errorOutlineSx } from "../../../../utils/formStyles";
import { EmploymentType, NurseFullRole, NurseRole, NurseTrainingStatus, WageType } from "../../../../utils/models";
import { enumToDropdownItems, getEmploymentTypeDisplayName } from "../../../../utils/utilFunctions";

const dropdownWidths = "95%";
const formControlSx = {
	width: dropdownWidths,
};

export type NurseFullRoleKeys = keyof typeof NurseFullRole;
export const getNurseRolesDropdownItems = (chosenDepartment: Department, roleColor: RoleColorResolver): DropdownItem<NurseFullRoleKeys>[] =>
	enumToDropdownItems(NurseFullRole)
		.filter((item) => chosenDepartment.roles.includes(NurseRole[item.value]))
		.map((item) => ({
			...item,
			color: roleColor(NurseFullRole[item.value]).dot,
			selectedLabel: item.value,
		}));

type NurseRolesDropdownProps = {
	selectedRole: NurseFullRoleKeys | undefined;
	onChange: (item?: NurseFullRoleKeys) => void;
	items: DropdownItem<NurseFullRoleKeys>[];
	error?: boolean;
};
export const NurseRolesDropdown: FC<NurseRolesDropdownProps> = ({ selectedRole, onChange, items, error }) => {
	return (
		<SystemDropdown
			formControlSx={formControlSx}
			selectSx={{
				...(error && errorOutlineSx),
			}}
			items={items}
			selectedItem={selectedRole}
			valueToString={(value) => NurseFullRole[value]}
			onChange={onChange}
		/>
	);
};

export type NurseEmploymentTypeKeys = keyof typeof EmploymentType;

export const getNurseEmploymentDropdownItems = (isSnf: boolean): DropdownItem<NurseEmploymentTypeKeys>[] => {
	return isSnf
		? enumToDropdownItems(EmploymentType, getEmploymentTypeDisplayName).filter((item) =>
				[EmploymentType.FullTime, EmploymentType.PRN, EmploymentType.PartTime, EmploymentType.PerDiem].includes(EmploymentType[item.value]),
			)
		: enumToDropdownItems(EmploymentType, getEmploymentTypeDisplayName);
};

// export const nurseEmploymentDropdownItems: DropdownItem<NurseEmploymentTypeKeys>[] = isSnf
// 	? enumToDropdownItems(EmploymentType, getEmploymentTypeDisplayName).filter((item) =>
// 			[EmploymentType.FullTime, EmploymentType.PRN, EmploymentType.PartTime, EmploymentType.PerDiem].includes(EmploymentType[item.value]),
// 		)
// 	: enumToDropdownItems(EmploymentType, getEmploymentTypeDisplayName);

type NurseEmploymentDropdownProps = {
	selectedType: NurseEmploymentTypeKeys;
	onChange: (item?: NurseEmploymentTypeKeys) => void;
};
export const NurseEmploymentTypeDropdown: FC<NurseEmploymentDropdownProps> = ({ selectedType, onChange }) => {
	const { isSnf } = useDepartmentFlags();
	const nurseEmploymentDropdownItems = getNurseEmploymentDropdownItems(isSnf);

	return (
		<SystemDropdown
			formControlSx={formControlSx}
			items={nurseEmploymentDropdownItems}
			selectedItem={selectedType}
			valueToString={(value) => EmploymentType[value]}
			onChange={onChange}
		/>
	);
};

export type NurseWageTypeKeys = keyof typeof WageType;
export const nurseWageTypeDropdownItems: DropdownItem<NurseWageTypeKeys>[] = enumToDropdownItems(WageType);
type NurseWageTypeDropdownProps = {
	selectedType?: NurseWageTypeKeys;
	onChange: (item?: NurseWageTypeKeys) => void;
	disabled?: boolean;
	dataTestId?: string;
};
export const NurseWageTypeDropdown: FC<NurseWageTypeDropdownProps> = ({ selectedType, onChange, disabled = false, dataTestId }) => {
	return (
		<SystemDropdown
			data-testid={dataTestId}
			formControlSx={formControlSx}
			items={nurseWageTypeDropdownItems}
			selectedItem={selectedType}
			valueToString={(value) => WageType[value]}
			onChange={onChange}
			disabled={disabled}
		/>
	);
};

export type TrainingStatusKeys = keyof typeof NurseTrainingStatus;
export const nurseTrainingStatusDropdownItems: DropdownItem<TrainingStatusKeys>[] = enumToDropdownItems(NurseTrainingStatus);
type NurseTrainingStatusDropdownProps = {
	selectedTrainingStatus: TrainingStatusKeys;
	onChange: (item?: TrainingStatusKeys) => void;
};
export const NurseTrainingStatusDropdown: FC<NurseTrainingStatusDropdownProps> = ({ selectedTrainingStatus, onChange }) => {
	return (
		<SystemDropdown
			formControlSx={formControlSx}
			items={nurseTrainingStatusDropdownItems}
			selectedItem={selectedTrainingStatus}
			valueToString={(value) => NurseTrainingStatus[value]}
			onChange={onChange}
		/>
	);
};

type AgencyDropdownProps = {
	selectedAgency?: string;
	onChange: (value?: string) => void;
	agencies: string[];
	onCreateAgency?: (agencyName: string) => void;
};

const CREATE_AGENCY_PREFIX = "__create__:";

export const AgencyDropdown: FC<AgencyDropdownProps> = ({ selectedAgency, onChange, agencies, onCreateAgency }) => {
	const agencyItems: SystemAutoCompleteOption<string>[] = agencies.map((agency) => ({
		label: agency,
		value: agency,
	}));

	return (
		<SystemAutoComplete
			options={agencyItems}
			fullWidth
			value={selectedAgency ? { label: selectedAgency, value: selectedAgency } : null}
			onChange={(_, value) => {
				if (value?.value.startsWith(CREATE_AGENCY_PREFIX)) {
					const newAgencyName = value.value.slice(CREATE_AGENCY_PREFIX.length);
					onCreateAgency?.(newAgencyName);
					onChange(newAgencyName);
				} else {
					onChange(value?.value);
				}
			}}
			filterOptions={(options, state) => {
				const filtered = options.filter((option) => option.label.toLowerCase().includes(state.inputValue.toLowerCase()));

				const inputValue = state.inputValue.trim();
				const exactMatch = filtered.some((option) => option.label.toLowerCase() === inputValue.toLowerCase());

				if (inputValue !== "" && !exactMatch && onCreateAgency) {
					filtered.push({
						label: `Add "${inputValue}"`,
						value: `${CREATE_AGENCY_PREFIX}${inputValue}`,
					});
				}

				return filtered;
			}}
			getOptionStartAdornment={(option) => {
				if (option.value.startsWith(CREATE_AGENCY_PREFIX)) {
					return "+";
				}
				return undefined;
			}}
			justifyContent={"start"}
			textFieldProps={{
				placeholder: "Select or type to add",
				reserveHeightForHelperText: false,
			}}
		/>
	);
};
